import { useState, useEffect, createContext, useContext } from 'react';
import { API_BASE_URL } from './useApi';

// Auth Context
const AuthContext = createContext(null);

const buildUser = (data = {}) => {
  const name = [data.first_name, data.last_name].filter(Boolean).join(' ').trim() || data.username || data.email || 'Usuário';
  const permissions = data.permissions || [];
  return {
    id: data.id,
    name,
    email: data.email,
    username: data.username,
    role: data.role || 'user',
    role_id: data.role_id,
    permissions,
    avatar: name[0]?.toUpperCase() || 'U',
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('nexus_token');
    if (token) {
      validateToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Token inválido');
      const data = await response.json();
      setUser(buildUser(data));
    } catch (error) {
      localStorage.removeItem('nexus_token');
      setUser(null);
      return;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: email, password })
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Erro no login' };
      if (data.mfa_required) return { success: false, mfaRequired: true, challenge: data.mfa_challenge };
      
      localStorage.setItem('nexus_token', data.access_token);
      setUser(buildUser(data.user || { email }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const completeMfaLogin = async (challenge, code) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_challenge: challenge, code }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Código inválido' };
      localStorage.setItem('nexus_token', data.access_token);
      setUser(buildUser(data.user || {}));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('nexus_token');
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (!permission) return true;
    if (user.role === 'admin') return true;
    const userPermissions = user.permissions || [];
    const [module] = permission.split(':');
    return (
      userPermissions.includes('*') ||
      userPermissions.includes(permission) ||
      userPermissions.includes(`${module}:*`)
    );
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfaLogin, logout, updateUser, hasPermission, isAuthenticated: Boolean(user) }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
